<template>
  <LayoutUser
    :columns="2"
    columns-reverse
    show-profile
    is-blog
  >
    <BLoading :active="isLoading" />
    <div
      class="c-blogs"
      v-if="!isLoading"
    >
      <div
        class="c-blogs_searchTitle"
        v-if="$route.query.archive"
      >
        <div v-if="isPC">アーカイブ：{{ archiveInfo }}</div>
        <div v-if="isSP || isTB">
          <div class="c-blogs_searchItem">アーカイブ</div>
          <div>{{ archiveInfo }}</div>
        </div>
      </div>
      <div
        class="c-blogs_searchTitle"
        v-if="$route.query.tag && blogs.blogsList"
      >
        <div v-if="isPC">タグ：{{ tagInfo }}</div>
        <div v-if="isSP || isTB">
          <div class="c-blogs_searchItem">タグ</div>
          <div>{{ tagInfo }}</div>
        </div>
      </div>
      <div
        v-for="(blog, index) in blogs.blogsList"
        :key="index"
        class="c-blogs_content"
      >
        <NuxtLink :to="`/blogs/${blog.userId}/${blog.id}`">
          <img
            v-if="blog.coverImageUrl"
            class="c-blogs_img"
            :src="blog.coverImageUrl"
            loading="lazy"
          />
          <div class="c-blogs_title">
            {{ $util.truncate(blog.title, maxTitle) }}
            <span class="c-blogs_kind">
              <CoconalaIcon
                :size="isPC ? '12px' : '10px'"
                :name="kindIconName(blog.kind)"
              />
              {{ kindText(blog.kind) }}
            </span>
          </div>
          <div class="c-blogs_category">{{ blog.blogCategory.name }}</div>
          <div class="c-blogs_summary">{{ $util.truncate(blog.summary, maxSummary) }}</div>
          <div
            v-if="blog.isPurchased"
            class="c-blogs_purchased"
          >購入済み</div>
          <div
            v-else-if="blog.price > 0"
            class="c-blogs_price"
          >{{ $util.numberWithDelimiter(blog.price) }}円</div>
          <div class="c-blogs_user">
            <CUserIconV2
              :file-path="user.resizableProfileIcon"
              :size="isPC ? 40 : 20"
            />
            <div class="c-blogs_writerDetail">
              <div class="c-blogs_writerName">{{ user.name }}</div>
              <div class="c-blogs_postDate">{{ $util.dayjs.unix(blog.openedAt).format('YYYY/MM/DD HH:mm') }}</div>
            </div>
          </div>
        </NuxtLink>
      </div>
      <CPagination
        class="c-blogs_pagination"
        order="is-centered"
        :total="blogs.total"
        :per-page="DEFAULT_PAGE_LIMIT"
        :current="Number($route.query.page || 1)"
        theme="pagination-gray"
        has-link
      />
    </div>
    <!-- SPの場合のサブエリアを下部に表示 -->
    <BlogSubArea v-if="isSP || isTB" />
    <TheVueport />
  </LayoutUser>
</template>

<script>
import Promise from 'bluebird'
Promise.config({ cancellation: true })

import LayoutUser from '~/components/templates/LayoutUser'
import CPagination from '~/components/molecules/CPagination'
import BlogSubArea from '~/components/organisms/BlogSubArea'
import TheVueport from '~/components/organisms/TheVueport'

// vuex
import { mapGetters, mapState } from 'vuex'

const DEFAULT_PAGE_LIMIT = 20
const MAX_TITLE_PC = 60
const MAX_SUMMARY_PC = 100
const MAX_TITLE_SP = 44
const MAX_SUMMARY_SP = 60

export default {
  components: {
    LayoutUser,
    CPagination,
    BlogSubArea,
    TheVueport
  },
  head() {
    return {
      htmlAttrs: {
        prefix: 'og: http://ogp.me/ns#'
      },
      __dangerouslyDisableSanitizers: ['script'],
      script: [
        {
          hid: 'json-ld-collection-page',
          type: 'application/ld+json',
          innerHTML: this.$util.escapeBreadcrumb(this.jsonLdCollectionPage)
        },
        {
          hid: 'json-ld-breadcrumb',
          type: 'application/ld+json',
          innerHTML: this.$util.escapeBreadcrumb(this.jsonLdBreadcrumb)
        }
      ],
      title: this.metaTitle,
      link: [
        {
          rel: 'canonical',
          content: this.$coconala.url(this.$route.path)
        }
      ],
      meta: this.meta
    }
  },
  async middleware({ app, store, params, query, error, redirect }) {
    const data = {
      userId: params.id,
      yearAndMonth: query.archive,
      blogTagId: query.tag,
      page: query.page
    }

    const promises = [
      new Promise((resolve, reject) => {
        store
          .dispatch('pages/users/user/fetchUser', {
            userId: data.userId
          })
          .then(resolve)
          .catch(reject)
      }),
      new Promise((resolve, reject) => {
        store
          .dispatch('pages/blogs/user/fetchBlogs', data)
          .then(resolve)
          .catch(reject)
      }),
      new Promise((resolve, reject) => {
        store
          .dispatch('pages/blogs/user/fetchBlogMenus', data.userId)
          .then(resolve)
          .catch(reject)
      })
    ]

    try {
      await Promise.all(promises)
    } catch (err) {
      promises.forEach(p => p.cancel())
      const statusCode = app.$util.grpcCodeToHttpStatusCode(err.grpcCode)
      return error({
        statusCode,
        message: '',
        grpcCode: err.grpcCode
      })
    }

    if (!store.state.pages.users.user.hasInfo.hasBlog) {
      // hasBlogがない場合は、プロフィールのhomeに返す
      redirect(`/users/${params.id}`)
    }
  },
  data() {
    return {
      DEFAULT_PAGE_LIMIT,
      isLoading: false
    }
  },
  watch: {
    $route() {
      this.fetchBlogs()
    }
  },
  computed: {
    ...mapGetters('ui', ['isPC', 'isSP', 'isTB']),
    ...mapState('pages/blogs/user', ['blogs', 'blogMenus']),
    ...mapState('pages/blogs/constants', ['KIND']),
    ...mapState('pages/users', ['user']),
    maxTitle() {
      return this.isPC ? MAX_TITLE_PC : MAX_TITLE_SP
    },
    maxSummary() {
      return this.isPC ? MAX_SUMMARY_PC : MAX_SUMMARY_SP
    },
    archiveInfo() {
      const queryArchive = String(this.$route.query.archive)
      const year = queryArchive.slice(0, 4)
      const month = queryArchive.slice(4, 6)
      const archive = this.$util.dayjs({ year, month: month - 1 })
      const yearInfo = this.blogMenus.menusByYearList.find(years => {
        return years.id == archive.year()
      })
      const monthInfo = yearInfo.menusByMonthList.find(months => {
        return months.id == archive.month() + 1
      })
      return `${this.$route.query.archive}(${monthInfo ? monthInfo.count : 0})`
    },
    tagInfo() {
      const tagInfo = this.blogMenus.menusByTagList.find(tag => {
        return tag.id == this.$route.query.tag
      })
      return tagInfo ? `${tagInfo.name}(${tagInfo.count})` : `${this.$route.query.tag}(0)`
    },
    meta() {
      const meta = [
        {
          hid: 'description',
          name: 'description',
          content: this.metaDescription
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: this.metaTitle
        },
        {
          hid: 'og:type',
          property: 'og:type',
          content: 'article'
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this.metaDescription
        },
        {
          hid: 'og:url',
          property: 'og:url',
          content: this.$coconala.url(this.$route.path)
        }
      ]

      // og:imageはカバー画像がある最新記事を設定、ない場合はDefault設定
      const coverImageBlog = this.blogs.blogsList.find(blog => {
        return blog.coverImageUrl
      })
      if (coverImageBlog) {
        meta.push({
          hid: 'og:image',
          property: 'og:image',
          content: coverImageBlog.coverImageUrl
        })
      }
      return meta
    },
    metaTitle() {
      let page = this.$route.query.page ? `(${this.$route.query.page}ページ目)` : ''
      return `${this.user.name}ブログ一覧${page}｜coconalaブログ`
    },
    metaDescription() {
      return `${
        this.user.name
      }のブログ一覧ページ。ココナラは、いろんなジャンルの「仕事」や「相談」をネットショッピングのように早く・簡単・おトクに依頼できます！ロゴや名刺などのデザイン、動画・サイト制作、お悩み相談など、ビジネスやプライベートで自分ではできないことをプロや専門家に依頼しませんか？No.1スキルマーケットのココナラ！`
    },
    jsonLdCollectionPage() {
      const jsonLd = {
        '@context': 'http://schema.org/',
        '@type': 'CollectionPage',
        mainEntityOfPage: this.$route.path,
        headline: `${this.user.name}ブログ一覧`,
        author: {
          '@type': 'Person',
          name: this.user.name,
          url: this.$coconala.url(`/users/${this.user.id}`)
        },
        publisher: {
          '@type': 'Organization',
          name: 'ココナラ',
          logo: {
            '@type': 'ImageObject',
            url: 'https://coconala.com/images/facebook.png',
            width: 250,
            height: 250
          }
        },
        description: this.metaDescription
      }
      // カバー画像がある最新記事を設定、ない場合はユーザー画像を設定
      const coverImageBlog = this.blogs.blogsList.find(blog => {
        return blog.coverImageUrl
      })
      if (coverImageBlog) {
        jsonLd['image'] = {
          '@type': 'ImageObject',
          url: coverImageBlog.coverImageUrl,
          width: 630,
          height: 354
        }
      } else {
        jsonLd['image'] = {
          '@type': 'ImageObject',
          url: this.$coconala.userIconURLV2(this.user.resizableProfileIcon, 200, 200),
          width: 200,
          height: 200
        }
      }
      return jsonLd
    },
    jsonLdBreadcrumb() {
      return {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': this.$coconala.url('/'),
              name: 'ココナラTOP'
            }
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': this.$coconala.url(this.$route.path),
              name: `${this.user.name}ブログ一覧`
            }
          }
        ]
      }
    }
  },
  methods: {
    async fetchBlogs() {
      const data = {
        userId: this.$route.params.id,
        yearAndMonth: this.$route.query.archive,
        blogTagId: this.$route.query.tag,
        page: this.$route.query.page
      }
      this.isLoading = true
      await this.$store.dispatch('pages/blogs/user/fetchBlogs', data).catch(grpcCode => {
        this.$notify({
          group: 'flash',
          title: 'システムエラーが発生しました',
          text: 'ブラウザを閉じて再度やり直してください',
          type: 'error',
          max: 1
        })
      })
      this.isLoading = false
    },
    isKindNormal(kind) {
      return kind === this.KIND.NORMAL
    },
    isKindContents(kind) {
      return kind === this.KIND.CONTENTS
    },
    isKindAnnounce(kind) {
      return kind === this.KIND.ANNOUNCE
    },
    kindIconName(kind) {
      if (this.isKindNormal(kind)) return 'blog-article'
      if (this.isKindContents(kind)) return 'blog-content'
      if (this.isKindAnnounce(kind)) return 'blog-announce'
      return 'blog-article'
    },
    kindText(kind) {
      return this.$store.getters['pages/blogs/constants/kindText'](kind)
    }
  }
}
</script>

<style lang="scss" scoped>
.c-blogs {
  &_content {
    margin-right: 24px;
    margin-bottom: 20px;
    padding: 24px;
    border: 1px solid $color-gray-200;
    border-radius: 5px;

    &:hover {
      background-color: $color-bg-pale;
    }
  }

  &_user {
    display: flex;
    margin-top: 12px;

    align-items: center;
  }

  &_kind {
    margin-left: 8px;
    color: $color-gray-600;
    font-size: 12px;
  }

  &_writerDetail {
    margin-left: 8px;
  }

  &_writerName {
    color: $color-gray-900;
    font-weight: bold;
    font-size: 16px;
  }

  &_postDate {
    color: $color-gray-600;
    font-size: 14px;
  }

  &_title {
    color: $color-gray-900;
    font-weight: bold;
    font-size: 20px;
  }

  &_category {
    margin-top: 8px;
    color: $color-gray-900;
    font-size: 10px;
  }

  &_summary {
    margin-top: 8px;
    color: $color-gray-700;
    font-size: 14px;
  }

  &_price,
  &_purchased {
    margin-top: 8px;
    font-size: 12px;
  }
  &_price {
    color: $color-gray-900;
  }
  &_purchased {
    color: $color-gray-600;
  }

  &_img {
    margin-bottom: 24px;
    width: 100%;
  }

  &_pagination {
    margin: 44px 0 32px;
  }

  &_searchTitle {
    margin-bottom: 28px;
    font-weight: bold;
    font-size: 20px;
  }
}

@media (max-width: $breakpoint-m) {
  .c-blogs {
    &_content {
      margin: 0;
      border: 0;
      border-bottom: 1px solid $color-gray-200;
      border-radius: 0;
    }

    &_writerName {
      color: $color-gray-900;
      font-weight: normal;
      font-size: 10px;
      line-height: 1.3;
    }

    &_postDate {
      color: $color-gray-600;
      font-size: 10px;
      line-height: 1.3;
    }

    &_title {
      font-size: 13px;
    }

    &_kind {
      font-size: 10px;
    }

    &_category {
      margin-top: 4px;
    }

    &_summary {
      font-size: 12px;
    }

    &_price,
    &_purchased {
      font-size: 10px;
    }

    &_pagination {
      margin-top: 32px;
      margin-bottom: 24px;
    }

    &_searchTitle {
      margin: 20px 16px 0;
    }

    &_searchItem {
      margin-top: 16px;
      color: $color-gray-600;
      font-size: 12px;
    }
  }
}
</style>
